import { useState, useEffect, useMemo, useCallback, useRef, memo } from 'react';
import {
  Box,
  Typography,
  Slider,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Alert,
  Tabs,
  Tab,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  DialogActions,
} from '@mui/material';
import { FixedSizeList as VirtualizedList, areEqual } from 'react-window';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  Timeline as ProgressIcon,
  VerifiedUser as QualityIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import {
  DialogContainer,
  DialogTitle,
  DialogBody,
  LoadingSkeleton,
  CustomButton,
  CustomTextArea,
} from '../../../../common/components';
import {
  useProjectProgressTracking,
  useBulkUpdateProgress,
} from '../../../../hooks/useProjectProgressTracking';
import {
  useProjectQualityTracking,
  useBulkUpdateQuality,
} from '../../../../hooks/useProjectQualityTracking';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`progress-tabpanel-${index}`}
      aria-labelledby={`progress-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

const qualityStatusOptions = [
  { value: 'not_started', label: 'Sin Iniciar', color: 'default' },
  { value: 'in_progress', label: 'En Progreso', color: 'info' },
  { value: 'completed', label: 'Completado', color: 'success' },
  { value: 'failed', label: 'Fallido', color: 'error' },
  { value: 'on_hold', label: 'En Espera', color: 'warning' },
];

const getQualityStatusInfo = (status) => {
  return qualityStatusOptions.find((option) => option.value === status) || qualityStatusOptions[0];
};

const ProgressRow = memo(({ index, style, data }) => {
  const [taskId, taskData] = data.items[index];
  return (
    <Box style={{ ...style, padding: '12px 0', boxSizing: 'border-box' }}>
      <Card elevation={1}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {taskData.task_name}
          </Typography>

          <Grid container spacing={4}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Progreso Actual: {taskData.current_percentage.toFixed(1)}%
                <Typography component="span" variant="caption" color="text.secondary">
                  {' '}
                  ({((taskData.current_percentage / 100) * taskData.max_percentage).toFixed(2)}% del
                  total)
                </Typography>
              </Typography>
              <Slider
                value={taskData.current_percentage}
                onChange={(e, value) => data.onChange(taskId, 'current_percentage', value)}
                aria-labelledby={`current-percentage-${taskId}`}
                step={0.1}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                color="primary"
                valueLabelFormat={(value) => `${value.toFixed(1)}%`}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}, areEqual);

const QualityRow = memo(({ index, style, data }) => {
  const [taskId, taskData] = data.items[index];
  const statusInfo = getQualityStatusInfo(taskData.status);

  return (
    <Box style={{ ...style, padding: '12px 0', boxSizing: 'border-box' }}>
      <Card elevation={1}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">{taskData.task_name}</Typography>
            <Chip label={statusInfo.label} color={statusInfo.color} size="small" />
          </Box>

          {taskData.phase && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Fase: {taskData.phase}
            </Typography>
          )}

          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" gutterBottom>
                Porcentaje de Calidad: {taskData.percentage}%
              </Typography>
              <Slider
                value={taskData.percentage}
                onChange={(e, value) => data.onChange(taskId, 'percentage', value)}
                aria-labelledby={`quality-percentage-${taskId}`}
                step={1}
                min={0}
                max={100}
                valueLabelDisplay="auto"
                color="success"
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <CustomTextArea
                label="Observaciones"
                name={`observations-${taskId}`}
                value={taskData.observations ?? ''}
                onChange={(e) => data.onChange(taskId, 'observations', e.target.value)}
                minRows={2}
                maxRows={2}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  value={taskData.status}
                  label="Estado"
                  onChange={(e) => data.onChange(taskId, 'status', e.target.value)}
                >
                  {qualityStatusOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}, areEqual);

/**
 * Mide la altura de un contenedor y se actualiza en resize.
 * @param {Object} ref - Ref del elemento a medir.
 * @param {Array} deps - Dependencias para recalcular la medición.
 * @returns {number} altura del contenedor en px.
 */
const useMeasuredHeight = (ref, deps = []) => {
  const [height, setHeight] = useState(400);

  useEffect(() => {
    if (!ref.current) return;
    const element = ref.current;

    const updateHeight = () => {
      const nextHeight = element.clientHeight;
      if (nextHeight) {
        setHeight(nextHeight);
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(element);
    window.addEventListener('resize', updateHeight);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateHeight);
    };
  }, deps);

  return height;
};

/**
 * Modal de gestión de progreso y calidad de proyecto
 * Separado en dos tabs independientes: Progreso de Obra y Control de Calidad
 */
const ProjectProgressDialog = ({ open, onClose, project }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [progressUpdates, setProgressUpdates] = useState({});
  const [qualityUpdates, setQualityUpdates] = useState({});
  const [hasProgressChanges, setHasProgressChanges] = useState(false);
  const [hasQualityChanges, setHasQualityChanges] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const progressListRef = useRef(null);
  const qualityListRef = useRef(null);
  const progressListHeight = useMeasuredHeight(progressListRef, [open, activeTab]);
  const qualityListHeight = useMeasuredHeight(qualityListRef, [open, activeTab]);

  const { data: progressData, isLoading: progressLoading } = useProjectProgressTracking(
    project?.uuid,
    { enabled: open && !!project }
  );
  const bulkUpdateProgressMutation = useBulkUpdateProgress();

  const { data: qualityData, isLoading: qualityLoading } = useProjectQualityTracking(
    project?.uuid,
    { enabled: open && !!project }
  );
  const bulkUpdateQualityMutation = useBulkUpdateQuality();

  useEffect(() => {
    if (progressData && open) {
      const initialValues = {};
      console.log(progressData);

      progressData.forEach((item) => {
        const maxPercentage = parseFloat(item.max_percentage) || 100;
        const currentPercentage = parseFloat(item.current_percentage) || 0;

        const currentRelative = maxPercentage > 0 ? (currentPercentage / maxPercentage) * 100 : 0;

        initialValues[item.uuid_task] = {
          current_percentage: Math.round(currentRelative * 100) / 100,
          original_current: Math.round(currentRelative * 100) / 100,
          task_name: item.task_name || 'Tarea sin nombre',
          max_percentage: maxPercentage,
        };
      });
      setProgressUpdates(initialValues);
      setHasProgressChanges(false);
    }
  }, [progressData, open]);

  useEffect(() => {
    if (qualityData && open) {
      const initialValues = {};
      console.log(qualityData);
      qualityData.forEach((item) => {
        initialValues[item.uuid_task] = {
          percentage: parseFloat(item.percentage) || 0,
          status: item.status || 'not_started',
          original_percentage: parseFloat(item.percentage) || 0,
          original_status: item.status || 'not_started',
          observations: item.observations ?? '',
          original_observations: item.observations ?? '',
          task_name: item.task_name || 'Tarea sin nombre',
          phase: item.phase || '',
          severity: item.severity || 'medium',
        };
      });
      setQualityUpdates(initialValues);
      setHasQualityChanges(false);
    }
  }, [qualityData, open]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const progressEntries = useMemo(
    () =>
      Object.entries(progressUpdates).sort(([, a], [, b]) =>
        (a.task_name || '').localeCompare(b.task_name || '')
      ),
    [progressUpdates]
  );

  const qualityEntries = useMemo(
    () =>
      Object.entries(qualityUpdates).sort(([, a], [, b]) =>
        (a.task_name || '').localeCompare(b.task_name || '')
      ),
    [qualityUpdates]
  );

  const handleProgressChange = useCallback((taskId, field, value) => {
    setProgressUpdates((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
    setHasProgressChanges(true);
  }, []);

  const handleQualityChange = useCallback((taskId, field, value) => {
    setQualityUpdates((prev) => ({
      ...prev,
      [taskId]: {
        ...prev[taskId],
        [field]: value,
      },
    }));
    setHasQualityChanges(true);
  }, []);

  const handleProgressSave = async () => {
    if (!hasProgressChanges) return;

    const updates = Object.entries(progressUpdates)
      .filter(([taskId, data]) => data.current_percentage !== data.original_current)
      .map(([taskId, data]) => {
        const currentAbsolute = (data.current_percentage / 100) * data.max_percentage;

        return {
          uuid_task: taskId,
          current_percentage: Math.round(currentAbsolute * 100) / 100,
        };
      });

    if (updates.length > 0) {
      try {
        await bulkUpdateProgressMutation.mutateAsync({
          projectId: project.uuid,
          progressUpdates: updates,
        });
        setHasProgressChanges(false);
      } catch (error) {
        console.error('Error al actualizar progreso:', error);
      }
    }
  };

  const handleQualitySave = async () => {
    if (!hasQualityChanges) return;

    const updates = Object.entries(qualityUpdates)
      .filter(
        ([taskId, data]) =>
          data.percentage !== data.original_percentage ||
          data.status !== data.original_status ||
          (data.observations ?? '') !== (data.original_observations ?? '')
      )
      .map(([taskId, data]) => ({
        uuid_task: taskId,
        percentage: parseFloat(data.percentage),
        status: data.status,
        observations: data.observations ?? '',
      }));

    if (updates.length > 0) {
      try {
        await bulkUpdateQualityMutation.mutateAsync({
          projectId: project.uuid,
          qualityUpdates: updates,
        });
        setHasQualityChanges(false);
      } catch (error) {
        console.error('Error al actualizar calidad:', error);
      }
    }
  };

  const handleClose = () => {
    setActiveTab(0);
    setHasProgressChanges(false);
    setHasQualityChanges(false);
    setProgressUpdates({});
    setQualityUpdates({});
    onClose();
  };

  const calculateProgressStats = () => {
    if (!progressUpdates || Object.keys(progressUpdates).length === 0) return null;

    const values = Object.values(progressUpdates);

    const totalMax = values.reduce((sum, item) => sum + item.max_percentage, 0);
    const totalCurrentAbsolute = values.reduce(
      (sum, item) => sum + (item.current_percentage / 100) * item.max_percentage,
      0
    );

    return {
      totalTasks: values.length,
      totalMax,
      totalCurrent: Math.round(totalCurrentAbsolute * 100) / 100,
      completionPercentage: totalMax > 0 ? (totalCurrentAbsolute / totalMax) * 100 : 0,
    };
  };

  const calculateQualityStats = () => {
    if (!qualityUpdates || Object.keys(qualityUpdates).length === 0) return null;

    const values = Object.values(qualityUpdates);
    const totalTasks = values.length;
    const completedTasks = values.filter((item) => item.status === 'completed').length;
    const failedTasks = values.filter((item) => item.status === 'failed').length;
    const inProgressTasks = values.filter((item) => item.status === 'in_progress').length;
    const averagePercentage =
      totalTasks > 0 ? values.reduce((sum, item) => sum + item.percentage, 0) / totalTasks : 0;

    return {
      totalTasks,
      completedTasks,
      failedTasks,
      inProgressTasks,
      averagePercentage,
      completionRate: totalTasks > 0 ? (completedTasks / totalTasks) * 100 : 0,
    };
  };

  const progressStats = calculateProgressStats();
  const qualityStats = calculateQualityStats();

  const progressItemData = useMemo(
    () => ({
      items: progressEntries,
      onChange: handleProgressChange,
    }),
    [progressEntries, handleProgressChange]
  );

  const qualityItemData = useMemo(
    () => ({
      items: qualityEntries,
      onChange: handleQualityChange,
    }),
    [qualityEntries, handleQualityChange]
  );

  const progressItemSize = isMobile ? 220 : 180;
  const qualityItemSize = isMobile ? 520 : 260;

  return (
    <DialogContainer open={open} onClose={handleClose} size="fullWidth">
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <ProgressIcon color="primary" />
          <Typography variant="h5">
            Seguimiento de Proyecto: {project?.name || 'Sin nombre'}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogBody sx={{ paddingBottom: '80px' }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange} aria-label="project tracking tabs">
            <Tab
              icon={<ProgressIcon />}
              label="Progreso de Obra"
              id="progress-tab-0"
              aria-controls="progress-tabpanel-0"
            />
            <Tab
              icon={<QualityIcon />}
              label="Control de Calidad"
              id="progress-tab-1"
              aria-controls="progress-tabpanel-1"
            />
          </Tabs>
        </Box>

        {/* PROGRESS TAB */}
        <TabPanel value={activeTab} index={0}>
          {progressLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {/* Progress Header Stats */}
              {progressStats && (
                <Card sx={{ mb: 3, bgcolor: 'primary.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="primary">
                      Resumen de Progreso
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          Total Tareas
                        </Typography>
                        <Typography variant="h6">{progressStats.totalTasks}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          Progreso
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          {progressStats.completionPercentage.toFixed(1)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={progressStats.completionPercentage}
                          sx={{ mt: 1 }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={4}>
                        <Typography variant="body2" color="text.secondary">
                          Estado
                        </Typography>
                        <Chip
                          label={hasProgressChanges ? 'Cambios pendientes' : 'Sincronizado'}
                          color={hasProgressChanges ? 'warning' : 'success'}
                          variant={hasProgressChanges ? 'filled' : 'outlined'}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {hasProgressChanges && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Tienes cambios de progreso sin guardar. No olvides hacer clic en "Guardar
                  Progreso".
                </Alert>
              )}

              <Box ref={progressListRef} sx={{ height: '60vh' }}>
                {progressEntries.length > 0 && (
                  <VirtualizedList
                    height={progressListHeight}
                    width="100%"
                    itemCount={progressEntries.length}
                    itemSize={progressItemSize}
                    itemData={progressItemData}
                    itemKey={(index, data) => data.items[index][0]}
                  >
                    {ProgressRow}
                  </VirtualizedList>
                )}
              </Box>
            </>
          )}
        </TabPanel>

        {/* QUALITY TAB */}
        <TabPanel value={activeTab} index={1}>
          {qualityLoading ? (
            <LoadingSkeleton />
          ) : (
            <>
              {/* Quality Header Stats */}
              {qualityStats && (
                <Card sx={{ mb: 3, bgcolor: 'success.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom color="success.main">
                      Resumen de Calidad
                    </Typography>
                    <Grid container spacing={3}>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Total Tareas
                        </Typography>
                        <Typography variant="h6">{qualityStats.totalTasks}</Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Completadas
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {qualityStats.completedTasks}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          En Progreso
                        </Typography>
                        <Typography variant="h6" color="info.main">
                          {qualityStats.inProgressTasks}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Fallidas
                        </Typography>
                        <Typography variant="h6" color="error.main">
                          {qualityStats.failedTasks}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Tasa Éxito
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {qualityStats.completionRate.toFixed(1)}%
                        </Typography>
                      </Grid>
                      <Grid item xs={12} sm={6} md={2}>
                        <Typography variant="body2" color="text.secondary">
                          Estado
                        </Typography>
                        <Chip
                          label={hasQualityChanges ? 'Cambios pendientes' : 'Sincronizado'}
                          color={hasQualityChanges ? 'warning' : 'success'}
                          variant={hasQualityChanges ? 'filled' : 'outlined'}
                        />
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>
              )}

              {hasQualityChanges && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  Tienes cambios de calidad sin guardar. No olvides hacer clic en "Guardar Calidad".
                </Alert>
              )}

              <Box ref={qualityListRef} sx={{ height: '60vh' }}>
                {qualityEntries.length > 0 && (
                  <VirtualizedList
                    height={qualityListHeight}
                    width="100%"
                    itemCount={qualityEntries.length}
                    itemSize={qualityItemSize}
                    itemData={qualityItemData}
                    itemKey={(index, data) => data.items[index][0]}
                  >
                    {QualityRow}
                  </VirtualizedList>
                )}
              </Box>
            </>
          )}
        </TabPanel>
      </DialogBody>

      <DialogActions
        sx={{
          position: 'sticky',
          bottom: 0,
          backgroundColor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          p: 2,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            gap: 1,
            flexShrink: 0,
            order: 1,
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
            marginLeft: 'auto',
          }}
        >
          {activeTab === 0 ? (
            <>
              <CustomButton
                onClick={handleProgressSave}
                disabled={!hasProgressChanges || bulkUpdateProgressMutation.isPending}
                variant="warning"
                buttonIcon={SaveIcon}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Guardar Progreso
              </CustomButton>
              <CustomButton
                onClick={handleClose}
                variant="danger"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Cerrar
              </CustomButton>
            </>
          ) : (
            <>
              <CustomButton
                onClick={handleQualitySave}
                disabled={!hasQualityChanges || bulkUpdateQualityMutation.isPending}
                variant="warning"
                buttonIcon={SaveIcon}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Guardar Calidad
              </CustomButton>
              <CustomButton
                onClick={handleClose}
                variant="danger"
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Cerrar
              </CustomButton>
            </>
          )}
        </Box>
      </DialogActions>
    </DialogContainer>
  );
};

export default ProjectProgressDialog;
